import { useCallback, useState } from 'react';
import type { Email } from '@/types/email';
import { trainMessageAsSpam, trainMessageAsGood, type TrainingRecord } from '@/api/trainingApi';
import { useEmailStore } from '@/stores/emailStore';

interface UseTrainMessageOptions {
  source?: string;
  onTrained?: (result: TrainingRecord) => void;
}

interface TrainMessageControls {
  trainAsSpam: () => Promise<TrainingRecord | null>;
  trainAsGood: () => Promise<TrainingRecord | null>;
  isTrainingSpam: boolean;
  isTrainingGood: boolean;
  canTrainSpam: boolean;
  canTrainGood: boolean;
}

export function useTrainMessage(email: Email | null, options: UseTrainMessageOptions = {}): TrainMessageControls {
  const [trainingState, setTrainingState] = useState({ spam: false, good: false });
  const updateEmailInPlace = useEmailStore((state) => state.updateEmailInPlace);

  const canTrainSpam = Boolean(email && !email.trainedAsSpam);
  const canTrainGood = Boolean(email && !email.trainedAsGood);

  const runTraining = useCallback(
    async (label: 'spam' | 'ham'): Promise<TrainingRecord | null> => {
      if (!email) {
        return null;
      }
      if (label === 'spam' && !canTrainSpam) {
        return null;
      }
      if (label === 'ham' && !canTrainGood) {
        return null;
      }
      setTrainingState((state) => ({
        ...state,
        [label === 'spam' ? 'spam' : 'good']: true,
      }));

      try {
        const trainer =
          label === 'ham'
            ? trainMessageAsGood
            : trainMessageAsSpam;
        const result = await trainer({
          accountId: email.accountId,
          messageId: email.id,
          source: options.source,
        });

        const timestamp = result.trainedAt ?? new Date().toISOString();
        const updates =
          label === 'ham'
            ? {
                trainedAsGood: result.status === 'trained' || result.alreadyTrained,
                lastTrainedLabel: 'ham' as const,
                lastTrainedAt: timestamp,
                lastTrainedSource: result.source ?? options.source ?? null,
              }
            : {
                trainedAsSpam: result.status === 'trained' || result.alreadyTrained,
                lastTrainedLabel: 'spam' as const,
                lastTrainedAt: timestamp,
                lastTrainedSource: result.source ?? options.source ?? null,
              };

        updateEmailInPlace(email.id, updates);

        options.onTrained?.(result);

        return result;
      } catch {
        return null;
      } finally {
        setTrainingState((state) => ({
          ...state,
          [label === 'spam' ? 'spam' : 'good']: false,
        }));
      }
    },
    [email, canTrainSpam, canTrainGood, options, updateEmailInPlace]
  );

  return {
    trainAsSpam: () => runTraining('spam'),
    trainAsGood: () => runTraining('ham'),
    isTrainingSpam: trainingState.spam,
    isTrainingGood: trainingState.good,
    canTrainSpam,
    canTrainGood,
  };
}
